import { CbhIcon, Text } from "@clipboard-health/ui-components";
import { useTheme } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { type TierType } from "@src/appV2/redesign/Tiers/api/schema";
import { BasePerk } from "@src/appV2/redesign/Tiers/PerksSection/BasePerk";
import { BasePerksSection } from "@src/appV2/redesign/Tiers/PerksSection/BasePerksSection";
import {
  DEFAULT_TIERS_WINDOWING_CONFIGURATION,
  LATE_CANCEL_PROTECTION_PERIOD_IN_HOURS,
  LATE_CANCEL_PROTECTION_SHIFT_PAYOUT_IN_HOURS,
} from "@src/appV2/redesign/Tiers/utils/constants";
import { getTextColorForTier } from "@src/appV2/redesign/Tiers/utils/getTextColorForTier";
import pluralize from "pluralize";

interface GoldPerksSectionProps {
  workerTier: TierType;
}

export function GoldPerksSection(props: GoldPerksSectionProps) {
  const { workerTier } = props;
  const theme = useTheme();
  const { maxWindowDurationInHours } = useCbhFlag(CbhFeatureFlag.CONFIG_TIERS_WINDOWING, {
    defaultValue: DEFAULT_TIERS_WINDOWING_CONFIGURATION,
  });
  const isLateCancellationProtectionEnabled = useCbhFlag(
    CbhFeatureFlag.UNIFY_LATE_CANCELLATION_POLICY_AND_PAYOUT,
    { defaultValue: false }
  );
  const matchesWorkerTier = workerTier === "GOLD";

  return (
    <BasePerksSection
      headerText={
        <Text semibold variant="h4" color={getTextColorForTier("GOLD", theme)}>
          Gold
        </Text>
      }
      icon={
        <CbhIcon
          type={matchesWorkerTier ? "membership-gold-colored" : "locked-filled"}
          size="large"
          sx={(theme) => ({
            transform: "scale(1.2)",
            ...(!matchesWorkerTier && { color: theme.palette.tiers?.gold.text }),
          })}
        />
      }
      perks={[
        <BasePerk
          key="priority-access"
          includeDividerBelow
          illustration={matchesWorkerTier ? "tiers-priority-small" : "tiers-gold-lock"}
          title="Extended Priority Access"
          description={
            <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
              You have up to {maxWindowDurationInHours}{" "}
              {pluralize("hour", maxWindowDurationInHours)} of early access to{" "}
              <Text semibold inline variant="body2" color={(theme) => theme.palette.text.secondary}>
                all
              </Text>{" "}
              shifts, including Urgent Shifts
            </Text>
          }
        />,
        isLateCancellationProtectionEnabled && (
          <BasePerk
            key="late-cancel-payout"
            includeDividerBelow
            illustration={matchesWorkerTier ? "tiers-shift-protection" : "tiers-green-lock"}
            title="Late Cancel Protection"
            description={`You are guaranteed ${LATE_CANCEL_PROTECTION_SHIFT_PAYOUT_IN_HOURS} ${pluralize(
              "hour",
              LATE_CANCEL_PROTECTION_SHIFT_PAYOUT_IN_HOURS
            )} of pay if a workplace cancels within ${LATE_CANCEL_PROTECTION_PERIOD_IN_HOURS} ${pluralize(
              "hour",
              LATE_CANCEL_PROTECTION_PERIOD_IN_HOURS
            )} of shift start`}
          />
        ),
        <BasePerk
          key="vip-support"
          illustration={matchesWorkerTier ? "phone-circle-with-diamond" : "tiers-blue-lock"}
          title="VIP Support"
          description="Instantly connect with Clipboard reps whenever you need help"
        />,
      ]}
      matchesWorkerTier={matchesWorkerTier}
    />
  );
}
